import React, {
   CSSProperties,
   FocusEvent,
   forwardRef,
   ReactElement,
   ReactNode,
   Ref,
   useCallback, useEffect,
   useMemo, useRef,
   useState
} from 'react';
import { DataTypeWithInvalid, formatDate, InvalidValue, parseDate } from '@unifocus/js-utils';
import { LocalDate } from '@js-joda/core';
import { InputRefType } from '../base-input/Input';
import { ValidatingInput } from '../validating-input/ValidatingInput';
import { DatePicker } from './DatePicker';
import { Popover } from '../../../display/popover';
import { Icons } from '@unifocus/react-icons';
import { useCombinedRefs } from '../../../../hooks';

export declare type DateInputValueType = DataTypeWithInvalid<LocalDate>;

/**
 * {@docCategory DateInput}
 */
export interface DateInputProps {

   /**
    * The name of the input item.
    */
   name?: string;

   /**
    * The value.
    */
   value?: DateInputValueType;

   /**
    * The placeholder text.
    */
   placeholder?: string;

   /**
    * Set the input item to read-only.
    * @defaultvalue false
    */
   readOnly?: boolean;

   /**
    * Set the input item to disabled.
    * @defaultvalue false
    */
   disabled?: boolean;

   /**
    * Sets the width.
    */
   width?: string | number;

   /**
    * Sets the min width.
    */
   minWidth?: string | number;

   /**
    * Sets the max width.
    */
   maxWidth?: string | number;

   /**
    * Sets the min date.
    */
   minDate?: LocalDate;

   /**
    * Sets the max date.
    */
   maxDate?: LocalDate;

   /**
    * Determines if a border should be shown around the component.
    * @defaultvalue true
    */
   border?: boolean;

   /**
    * Determines if the component should have internal padding.
    * @defaultvalue true
    */
   padding?: boolean;

   /**
    * Show the clear button. The clear button will only be shown if there is a value in the input item.
    * @defaultvalue false
    */
   allowClear?: boolean;

   /**
    * The prefix element.
    */
   prefix?: ReactNode;

   /**
    * The suffix element.
    */
   suffix?: ReactNode;

   /**
    * The label text displayed before (on the left side of) the input field.
    */
   addonBefore?: ReactNode;

   /**
    * The label text displayed after (on the right side of) the input field.
    */
   addonAfter?: ReactNode;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles.
    */
   style?: CSSProperties;

   /**
    * Set to hide the picker.
    * @default false
    */
   hideCalendarPicker?: boolean;

   /**
    * To show or hide today button in the popup calendar.
    * @default true
    */
   showTodayButton?: boolean;

   /**
    * format cells in the popup calendar.
    */
   formatCell?: (date: LocalDate) => ReactElement | null;

   /**
    * Set this to focus on first render
    */
   autoFocus?: boolean;

   /**
    * Callback called when the value is changed.
    * @param value - The value
    */
   onChange?: (value?: DateInputValueType) => void;

   /**
    * Callback called when the component loses focus.
    * @param event - The focus event object
    */
   onBlur?: (event: FocusEvent<HTMLInputElement>) => void;

   /**
    * Callback called when the component gains focus.
    * @param event - The focus event object
    */
   onFocus?: (event: FocusEvent<HTMLInputElement>) => void;

   /**
    * Function called when the user presses the enter key.
    */
   onPressEnter?: React.KeyboardEventHandler<HTMLInputElement>;

   /**
    * Function called when the dirty state changes.  The input is considered dirty
    * when the raw string value that is currently typed into the field does not
    * match the last good validated value.
    *
    * @param isDirty - True if the raw string value that is currently typed into the field does not match the last good validated value
    */
   onDirtyStateChange?: (isDirty: boolean) => void;
}

export const DateInput = forwardRef<InputRefType, DateInputProps>(
   (props: DateInputProps, ref: Ref<InputRefType>) => {

      const inputRef = useRef<InputRefType>(null);
      const combinedRef = useCombinedRefs<InputRefType>(inputRef, ref);

      const [value, setValue] = useState<DateInputValueType | undefined>(props.value);

      useEffect(() => {
         setValue(props.value);
      }, [props.value]);

      const { onChange: propsOnChange, minDate, maxDate, disabled, readOnly, onBlur } = props;
      const onChangeHandler = useCallback((newValue?: DateInputValueType) => {
         setValue(newValue);
         if (propsOnChange) {
            propsOnChange(newValue);
         }
      }, [propsOnChange]);

      const onPickerChangeHandler = useCallback((newValue?: DateInputValueType) => {
         onChangeHandler(newValue);
         if (combinedRef && combinedRef.current) {
            combinedRef.current.focus();
         }
      }, [combinedRef, onChangeHandler]);

      const popupTrigger = useMemo(
         () => {
            return (<DatePickerTrigger value={value} disabled={disabled} readOnly={readOnly} onChange={onPickerChangeHandler} minDate={minDate} maxDate={maxDate}/>);
         },
         [value, minDate, maxDate, disabled, readOnly, onPickerChangeHandler ]);

      return (
         <ValidatingInput<LocalDate>
            ref={combinedRef}
            name={props.name}
            type={'text'}
            inputMode={'text'}
            placeholder={props.placeholder}
            readOnly={readOnly}
            disabled={disabled}
            allowClear={props.allowClear}
            width={props.width}
            minWidth={props.minWidth}
            maxWidth={props.maxWidth}
            border={props.border}
            padding={props.padding}
            prefix={props.prefix}
            suffix={props.suffix}
            addonBefore={props.addonBefore}
            addonAfter={props.addonAfter}
            popupTrigger={props.hideCalendarPicker ? undefined : popupTrigger}
            format={dateInputFormatter()}
            parse={dateInputParser()}
            inputValidator={dateInputValidator()}
            className={props.className}
            style={props.style}
            value={value}
            autoFocus={props.autoFocus}
            onChange={onChangeHandler}
            onBlur={onBlur}
            onFocus={props.onFocus}
            onPressEnter={props.onPressEnter}
            onDirtyStateChange={props.onDirtyStateChange}
         />
      );
   }
);

interface DatePickerTriggerProps {
   value?: DateInputValueType;
   disabled?: boolean;
   readOnly?: boolean;
   onChange?: (value?: DateInputValueType) => void;
   minDate?: LocalDate;
   maxDate?: LocalDate;
   formatCell?: (date: LocalDate) => ReactElement | null;
}

const DatePickerTrigger = (props: DatePickerTriggerProps) => {
   const [datePickerVisible, setDatePickerVisible] = useState(false);

   const [count, setCount] = useState(0);

   const { onChange: propsOnChange, minDate, maxDate, value, formatCell } = props;
   const datePickerValue = value instanceof LocalDate ? value : undefined;

   const onOk = useCallback((value: LocalDate | undefined) => {
      if (propsOnChange) {
         propsOnChange(value);
      }
      setDatePickerVisible(false);
      setCount((count) => (count+1));
   },[propsOnChange]);

   const onCancel = useCallback(() => {
      setDatePickerVisible(false);
      setCount((count) => (count+1));
   },[]);

   const onVisibleChange = useCallback((visible: boolean) => {
      setDatePickerVisible(visible);
      if (!visible) {
         setCount((count) => (count+1));
      }
   },[]);

   const datePicker = useMemo(()=> {
      return (<DatePicker
         key={count}
         value={datePickerValue}
         onOk={onOk}
         onCancel={onCancel}
         minDate={minDate}
         maxDate={maxDate}
         formatCell={formatCell}
      />);
   },[count, datePickerValue, onOk, onCancel, minDate, maxDate, formatCell]);

   return (
      (props.disabled || props.readOnly) ? <Icons.Calendar/> :
      <Popover
         overlayClassName={'uf-date-input-picker-popover'}
         visible={datePickerVisible}
         onVisibleChange={onVisibleChange}
         trigger={'click'}
         content={datePicker}
      >
         <Icons.Calendar/>
      </Popover>
   );
};

export const dateInputFormatter = () => (value?: LocalDate): string => {
   if (value === undefined) {
      return '';
   }

   return formatDate(value);
};

export const dateInputParser = () => (value?: string): DateInputValueType => {
   if (value === undefined || value.trim().length === 0) {
      return undefined;
   }

   try {
      return parseDate(value);
   }
   catch (error) {
      return InvalidValue;
   }
};

export const dateInputValidator = () => {
   const regExp = /^\d{1,4}([./-]\d{0,2}([./-]\d{0,4})?)?$/;

   return (value?: string): boolean => {
      if (value) {
         return regExp.test(value);
      }

      return true;
   };
};
